import { numberToPrettyString } from '@cian/utils';
import { CommercialClassName, IOffer, LandAreaUnitType, TOfferCategory } from '../types/offer';
import { getCommercialCoworkingTitle } from '../utils/getCoworkingTypeTitle';

export interface ICommercialParams {
  isOfficeSearch?: boolean;
  isOfficeInCoworkingTitleVisible?: boolean;
  isFlexOfficeCardSameAsOffice?: boolean;
}

export interface ICommercialHelper {
  getBuildingClassType(): string | void;
  getBuildingType(): string;
  getCommercialLandArea(): string | number | null;
  getCommercialLandPerMeterByYear(): string | null;
  getCommercialLandUnit(): ICommercialLandUnit | null;
  getCommercialLandUnitType(): LandAreaUnitType | null;
  getMinArea(): string | void;
  getSpecialty(): string[];
  getType(params: ICommercialParams): string;
  getVatTypeWithVatPrice(): string | void;
  isCommercialLand(): boolean | void;
}

export function commercialHelper(offer: IOffer): ICommercialHelper {
  return {
    getBuildingClassType: () => getBuildingClassType(offer),
    getBuildingType: () => getBuildingType(offer),
    getCommercialLandArea: () => getCommercialLandArea(offer),
    getCommercialLandPerMeterByYear: () => getCommercialLandPerMeterByYear(offer),
    getCommercialLandUnit: () => getCommercialLandUnit(offer),
    getCommercialLandUnitType: () => getCommercialLandUnitType(offer),
    getMinArea: () => getMinArea(offer),
    getSpecialty: () => getSpecialty(offer),
    getType: (params) => getType(offer, params),
    getVatTypeWithVatPrice: () => getVatTypeWithVatPrice(offer),
    isCommercialLand: () => isCommercialLand(offer),
  };
}

const buildingTypes: { [key: string]: string } = {
  administrativeBuilding: 'Административное здание',
  businessCenter: 'Бизнес-центр',
  businessCenter2: 'Деловой центр',
  businessPark: 'Бизнес-парк',
  businessQuarter: 'Бизнес-квартал',
  free: 'Свободное',
  freeAppointmentObject: 'Объект свободного назначения',
  industrialComplex: 'Производственный комплекс',
  industrialPark: 'Индустриальный парк',
  industrialSite: 'Промплощадка',
  industrialWarehouseComplex: 'Производственно-складской комплекс',
  logisticsCenter: 'Логистический центр',
  logisticsComplex: 'Логистический комплекс',
  mansion: 'Особняк',
  manufactureBuilding: 'Производственное здание',
  manufacturingFacility: 'Производственный цех',
  modular: 'Модульное здание',
  multifunctionalComplex: 'Многофункциональный комплекс',
  officeAndHotelComplex: 'Офисно-гостиничный комплекс',
  officeAndResidentialComplex: 'Офисно-жилой комплекс',
  officeAndWarehouse: 'Офисно-складское',
  officeAndWarehouseComplex: 'Офисно-складской комплекс',
  officeBuilding: 'Офисное здание',
  officeIndustrialComplex: 'Офисно-производственный комплекс',
  old: 'Старый фонд',
  outlet: 'Аутлет',
  propertyComplex: 'Имущественный комплекс',
  residentialComplex: 'Жилой комплекс',
  residentialFund: 'Жилой фонд',
  residentialHouse: 'Жилой дом',
  shoppingAndBusinessComplex: 'Торгово-деловой комплекс',
  shoppingAndCommunityCenter: 'Торгово-общественный центр',
  shoppingAndEntertainmentCenter: 'Торгово-развлекательный центр',
  shoppingCenter: 'Торговый центр',
  specializedShoppingCenter: 'Специализированный торговый центр',
  standaloneBuilding: 'Отдельно стоящее здание',
  technopark: 'Технопарк',
  tradingOfficeComplex: 'Торгово-офисный комплекс',
  uninhabitedFund: 'Нежилой фонд',
  warehouse: 'Склад',
  warehouseComplex: 'Складской комплекс',
};

function getType(offer: IOffer, params: ICommercialParams) {
  return findTypeById([
    createSearchType('office', () => getCommercialCoworkingTitle(offer, 'Офис', params)),
    createSearchType('shoppingArea', () => 'Торговая площадь'),
    createSearchType('warehouse', () => 'Склад'),
    createSearchType('freeAppointmentObject', () => 'Своб. назнач.'),
    createSearchType('industry', () => 'Помещение под производство'),
    createSearchType('publicCatering', () => 'Общепит'),
    createSearchType('manufacture', () => 'Производство'),
    createSearchType('autoService', () => 'Автосервис'),
    createSearchType('business', () => 'Готовый бизнес'),
    createSearchType('building', () => 'Здание'),
    createSearchType('domesticServices', () => 'Бытовые услуги'),
    createSearchType('commercialLand', () => 'Коммерческая земля'),
    createSearchType('garage', () => {
      if (!offer.garage) {
        return 'Гараж';
      }

      switch (offer.garage.type) {
        case 'box':
          return 'Бокс';
        case 'garage':
          return 'Гараж';
        case 'parkingPlace':
          return 'Машиноместо';
        default:
          return '';
      }
    }),
  ], offer);
}

interface IOfferSearchType {
  getRightName(): string;
  name: string;
}

function createSearchType(name: string, getRightName: () => string): IOfferSearchType {
  return {
    getRightName,
    name,
  };
}

function findTypeById(values: IOfferSearchType[], offer: IOffer) {
  return values.reduce((acc, item) => {

    if (offer.category === item.name + 'Rent') {
      return item.getRightName();
    } else if (offer.category === item.name + 'Sale') {
      return item.getRightName();
    }

    return acc;
  }, '');
}

function getSpecialty(offer: IOffer) {
  if (offer.specialty && offer.specialty.specialties) {
    return offer.specialty.specialties.reduce((acc: string[], value) => {
      if (value.rusName && acc.length < 2) {
        acc.push(value.rusName.toLowerCase());
      }

      return acc;
    }, []);
  }

  return [];
}

function getBuildingType(offer: IOffer) {
  if (offer.building && offer.building.type) {
    const type = buildingTypes[offer.building.type];

    return type ? type.toLowerCase() : '';
  }

  return '';
}

function getBuildingClassType(offer: IOffer) {
  if (offer.businessShoppingCenter && offer.businessShoppingCenter.buildingClassType) {
    return CommercialClassName.get(offer.businessShoppingCenter.buildingClassType);
  }

  return undefined;
}

function getMinArea(offer: IOffer): string | undefined {
  return offer.minArea ? offer.minArea.split('.')[0] : undefined;
}

function isCommercialLand(offer: IOffer) {
  return offer.category && ([
    'commercialLandRent',
    'commercialLandSale',
  ] as TOfferCategory[]).includes(offer.category);
}

export interface ICommercialLandUnit {
  text: string;
  type: LandAreaUnitType;
}

function getCommercialLandUnit(offer: IOffer): ICommercialLandUnit | null {
  if (!offer.land || !offer.land.areaUnitType) {
    return null;
  }

  return {
    text: offer.land.areaUnitType === 'sotka' ? 'сот.' : 'га.',
    type: offer.land.areaUnitType,
  };
}

function getCommercialLandPerMeterByYear(offer: IOffer) {
  if (!offer.pricePerUnitAreaPerYearRur) {
    return null;
  }

  const unit = getCommercialLandUnit(offer);

  return `${numberToPrettyString(offer.pricePerUnitAreaPerYearRur)} руб. за ${unit ? unit.text : 'м\u00b2'} в год`;
}

function getCommercialLandArea(offer: IOffer) {
  return offer.land && offer.land.area ? offer.land.area : null;
}

function getCommercialLandUnitType(offer: IOffer) {
  return offer.land && offer.land.areaUnitType ? offer.land.areaUnitType : null;
}

function getVatPrice(to?: number | null, from?: number | null) {
  switch (true) {
    case Boolean(to):
      return `${to} руб.`;
    case Boolean(from):
      return `${from} руб.`;
    default:
      return '';
  }
}

function getVatTypeWithVatPrice(offer: IOffer) {
  const { bargainTerms,
    minVatPriceTotalRur,
    vatPriceTotalRur,
    minVatPriceTotalPerMonthRur,
    vatPriceTotalPerMonthRur,
  }  = offer;
  const { vatType } = bargainTerms;

  switch (vatType) {
    case 'usn':
      return 'УСН';
    case 'included':
      if (offer.dealType === 'sale') {
        return `Включая НДС ${getVatPrice(vatPriceTotalRur, minVatPriceTotalRur)}`;
      } else if (offer.dealType === 'rent') {
        return `Включая НДС ${getVatPrice(vatPriceTotalPerMonthRur, minVatPriceTotalPerMonthRur)}`;
      } else {
        return 'Включая НДС';
      }
    default:
      return undefined;
  }
}
